// pages/public/HomePage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../../layout/PublicLayout';
import ProductCard from '../../components/public/ProductCard';
import { getProductosPublicos, getRestauranteActual } from '../../api/public';
import { useTypewriterColored } from '../../hooks/useTypewriterColored';
import { ChevronRight, Star, Clock, Award } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [productosDestacados, setProductosDestacados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  
  // Efecto typewriter para toda la frase con colores diferentes
  // "Sabor que " tiene 10 caracteres (incluyendo el espacio)
  const { part1, part2 } = useTypewriterColored('Sabor que Conquista', 10, 100, 50, 2000);

  useEffect(() => {
    const loadProductos = async () => {
      try {
        const productos = await getProductosPublicos();
        // Mostrar solo los primeros 6 productos
        setProductosDestacados(productos.slice(0, 6));

        // Cargar datos del restaurante para obtener el WhatsApp
        const restauranteData = await getRestauranteActual();
        if (restauranteData && restauranteData.telefono) {
          setWhatsappNumber(restauranteData.telefono);
        }
      } catch (error) {
        console.error('Error cargando productos:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProductos();
  }, []);

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-red-600 to-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold mb-6 leading-tight md:whitespace-nowrap">
                <span className="text-white">{part1}</span>
                <span className="text-yellow-300">
                  {part2}
                  <span className="animate-pulse">|</span>
                </span>
              </h1>
              <p className="text-xl mb-8 text-red-50">
                Descubre nuestra deliciosa variedad de platos preparados con ingredientes frescos 
                y el amor de siempre.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('/menu')}
                  className="bg-white text-red-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-300 hover:text-red-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  Ver Menú
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => navigate('/promociones')}
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-red-600 transition-all"
                >
                  Promociones
                </button>
              </div>
            </div>
            
            {/* Imagen de Plato Típico Ecuatoriano */}
            <div className="hidden md:flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-300 rounded-full blur-3xl opacity-30"></div>
                
                {/* Imagen de Pollo Asado - Plato típico de asadero ecuatoriano */}
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1598103442097-8b74394b95c6?w=500&h=500&fit=crop" 
                    alt="Pollo Asado Ecuatoriano" 
                    className="w-96 h-96 object-cover rounded-full shadow-2xl animate-float"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave decoration */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Entrega Rápida</h3>
              <p className="text-gray-600">
                Tu pedido listo en tiempo récord sin comprometer la calidad
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Calidad Premium</h3>
              <p className="text-gray-600">
                Ingredientes frescos y de primera calidad en cada plato
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Servicio Excepcional</h3>
              <p className="text-gray-600">
                Atención personalizada que supera tus expectativas
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Productos Destacados */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nuestros <span className="text-red-600">Favoritos</span>
            </h2>
            <p className="text-xl text-gray-600">
              Los platos más populares que nuestros clientes aman
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
              <p className="mt-4 text-gray-600">Cargando productos...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {productosDestacados.map((producto) => (
                  <ProductCard 
                    key={producto.id_producto} 
                    producto={producto} 
                    whatsappNumber={whatsappNumber}
                  />
                ))}
              </div>

              <div className="text-center mt-12">
                <button
                  onClick={() => navigate('/menu')}
                  className="bg-red-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-red-700 transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2"
                >
                  Ver Menú Completo
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            ¿Listo para disfrutar?
          </h2>
          <p className="text-xl mb-8 text-red-50">
            Encuentra el restaurante más cercano y haz tu pedido ahora
          </p>
          <button
            onClick={() => navigate('/restaurantes')}
            className="bg-white text-red-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-yellow-300 hover:text-red-700 transition-all shadow-lg hover:shadow-xl"
          >
            Encontrar Restaurante
          </button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default HomePage;
